// Entity registry: who is in the log and who owns whom.
//
// Identity is built on GUIDs and numeric ids, never on the localized names that
// appear in the log, so a Russian or German client produces the same structure
// as an English one.

import { UNIT_FLAGS, describeSpec, classColor } from './constants'
import { parseGrouped, unquote } from './tokenizer'

const REGION_PATTERN = /^(EU|US|KR|TW|CN)$/i
const COMBATANT_SPEC_INDEX = 25

export function parseGuid(guid) {
    if (!guid || guid === '0000000000000000') return { type: 'none', npcId: 0 }
    const dash = guid.indexOf('-')
    if (dash < 0) return { type: 'unknown', npcId: 0 }

    const type = guid.slice(0, dash)
    if (type === 'Player') return { type, npcId: 0 }

    const parts = guid.split('-')
    const npcId = parts.length >= 6 ? Number(parts[5]) || 0 : 0
    return { type, npcId }
}

// "Ёжиквтумане-Ревущийфьорд-EU" -> { name, realm, region }
export function splitCharacterName(fullName) {
    if (!fullName) return { name: '', realm: '', region: '' }
    const parts = fullName.split('-')
    if (parts.length === 1) return { name: parts[0], realm: '', region: '' }

    let region = ''
    if (REGION_PATTERN.test(parts[parts.length - 1])) {
        region = parts.pop().toUpperCase()
    }

    const name = parts.shift()
    return { name, realm: parts.join('-'), region }
}

function createActor(guid, name, flags) {
    const { type, npcId } = parseGuid(guid)
    const isPlayer = type === 'Player'
    const isPet = type === 'Pet'
        || (flags & UNIT_FLAGS.TYPE_PET) === UNIT_FLAGS.TYPE_PET
        || (flags & UNIT_FLAGS.TYPE_GUARDIAN) === UNIT_FLAGS.TYPE_GUARDIAN
    const character = isPlayer ? splitCharacterName(name) : null

    return {
        guid,
        name: name || '',
        shortName: character ? character.name : name || '',
        realm: character ? character.realm : '',
        region: character ? character.region : '',
        unitType: type,
        npcId,
        flags,
        isPlayer,
        isPet,
        isNpc: !isPlayer && !isPet,
        isFriendly: (flags & UNIT_FLAGS.REACTION_FRIENDLY) === UNIT_FLAGS.REACTION_FRIENDLY,
        isGroupMember: isPlayer && (flags & (UNIT_FLAGS.AFFILIATION_MINE | UNIT_FLAGS.AFFILIATION_PARTY | UNIT_FLAGS.AFFILIATION_RAID)) !== 0,
        isLogOwner: isPlayer && (flags & UNIT_FLAGS.AFFILIATION_MINE) === UNIT_FLAGS.AFFILIATION_MINE,
        ownerGuid: '',
        specId: 0,
        className: '',
        spec: '',
        role: '',
        color: '',
        level: 0,
        itemLevel: 0,
        maxHp: 0,
        seen: 0,
    }
}

export function createRegistry(initial) {
    const actors = initial && initial.actors instanceof Map ? initial.actors : new Map()
    let logOwnerGuid = (initial && initial.logOwnerGuid) || ''

    function observe(guid, name, flags) {
        if (!guid || guid === '0000000000000000') return null
        let actor = actors.get(guid)
        if (!actor) {
            actor = createActor(guid, name, flags)
            actors.set(guid, actor)
        } else {
            if (name && actor.name !== name) {
                actor.name = name
                if (actor.isPlayer) {
                    const character = splitCharacterName(name)
                    actor.shortName = character.name
                    actor.realm = character.realm
                    actor.region = character.region
                } else {
                    actor.shortName = name
                }
            }
            if (flags) {
                actor.flags |= flags
                if (!actor.isGroupMember && actor.isPlayer) {
                    actor.isGroupMember = (actor.flags & (UNIT_FLAGS.AFFILIATION_MINE | UNIT_FLAGS.AFFILIATION_PARTY | UNIT_FLAGS.AFFILIATION_RAID)) !== 0
                }
                if (!actor.isLogOwner && actor.isPlayer) {
                    actor.isLogOwner = (actor.flags & UNIT_FLAGS.AFFILIATION_MINE) === UNIT_FLAGS.AFFILIATION_MINE
                }
            }
        }

        actor.seen += 1
        if (actor.isLogOwner && !logOwnerGuid) logOwnerGuid = guid
        return actor
    }

    function setOwner(guid, ownerGuid) {
        if (!guid || !ownerGuid || guid === ownerGuid) return
        if (ownerGuid === '0000000000000000') return
        const actor = actors.get(guid)
        if (!actor || actor.isPlayer) return
        if (!actor.ownerGuid) actor.ownerGuid = ownerGuid
    }

    // Walks pet -> owner chains (a guardian summoned by a pet keeps pointing at
    // the player) and stops on cycles.
    function resolveOwner(guid) {
        let current = guid
        for (let hops = 0; hops < 4; hops += 1) {
            const actor = actors.get(current)
            if (!actor || !actor.ownerGuid) return current
            if (actor.ownerGuid === current) return current
            current = actor.ownerGuid
        }
        return current
    }

    function applyCombatantInfo(fields) {
        const guid = fields[1]
        if (!guid) return null
        const actor = observe(guid, '', UNIT_FLAGS.TYPE_PLAYER | UNIT_FLAGS.AFFILIATION_PARTY)
        if (!actor) return null

        const specId = Number(fields[COMBATANT_SPEC_INDEX]) || 0
        if (specId) {
            const spec = describeSpec(specId)
            actor.specId = specId
            actor.className = spec.className
            actor.spec = spec.spec
            actor.role = spec.role
            actor.color = classColor(spec.className)
        }

        return actor
    }

    // COMBATANT_INFO also carries the equipped gear list; the average item level
    // is the only part of it the analyzer surfaces.
    function readCombatantItemLevel(fields) {
        const gearField = fields.find((field) => typeof field === 'string' && field.startsWith('['))
        if (!gearField) return 0
        const parsed = parseGrouped(gearField)
        if (!Array.isArray(parsed)) return 0

        let sum = 0
        let count = 0
        parsed.forEach((item) => {
            if (!Array.isArray(item)) return
            const itemLevel = Number(item[1]) || 0
            if (itemLevel > 0) {
                sum += itemLevel
                count += 1
            }
        })
        return count ? Math.round(sum / count) : 0
    }

    return {
        actors,
        get logOwnerGuid() {
            return logOwnerGuid
        },
        get logOwner() {
            return logOwnerGuid ? actors.get(logOwnerGuid) : null
        },
        observe,
        setOwner,
        resolveOwner,
        get(guid) {
            return actors.get(guid) || null
        },
        applyCombatantInfo,
        readCombatantItemLevel,
        players() {
            return [...actors.values()].filter((actor) => actor.isPlayer)
        },
        groupMembers() {
            return [...actors.values()].filter((actor) => actor.isPlayer && actor.isGroupMember)
        },
        serialize() {
            return { actors, logOwnerGuid }
        },
    }
}

export function unitDisplayName(actor) {
    if (!actor) return 'Unknown'
    return actor.isPlayer ? actor.shortName || actor.name : actor.name
}

export { unquote }
